import React, { useState, useRef, useEffect } from "react";
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import SvgComponent from "../../SVGComponent";
import "./index.css";
import { TimePickerConfiguration } from "../../../../utils/configurationProvider";
import ICONS from "../../../../assets/Icons";

/**
 * CommonTimePicker Component
 * @component
 *
 * @param {Object} props - Component props.
 *
 * @param {Function} props.handleTimeConfirm - Callback invoked when user confirms the time.
 * Receives parameters: (fieldname, data_type, hours, minutes, meridiem).
 *
 * @param {Function} props.handleClearTime - Callback invoked when user clears the time.
 * Receives parameters: (keyTime, fieldname).
 *
 * @param {string} props.value - Display value for the main collapsed picker (e.g., "10:30 AM").
 *
 * @param {string} props.data_type - Data type identifier passed back to the parent handler.
 *
 * @param {string} [props.fieldname] - Field name associated with this time picker (optional).
 *
 * @param {string} [props.keyTime] - Key used inside `timeData` to identify this specific time field (optional).
 *
 * @param {Object} props.timeData - Object storing selected time values, shaped like:
 * `{ [keyTime]: { hours: string, minutes: string, meridiem: string } }`.
 *
 * @param {Function} props.setTimeData - State setter for updating the timeData object.
 *
 */

const CommonTimePicker = ({
  handleTimeConfirm,
  handleClearTime,
  value,
  data_type,
  fieldname,
  keyTime,
  timeData,
  setTimeData,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const wrapperRef = useRef(null);

  const hours = Array.from({ length: TimePickerConfiguration.hour }, (_, i) =>
    (i + 1).toString().padStart(2, "0"),
  );

  const minutes = Array.from(
    { length: TimePickerConfiguration.minute },
    (_, i) => i.toString().padStart(2, "0"),
  );

  const meridiems = TimePickerConfiguration.meridiem.map((m) =>
    m.toUpperCase(),
  );

  const dropdownRefs = {
    hour: useRef(null),
    minute: useRef(null),
    meridiem: useRef(null),
  };

  const toggleDropdown = (type) => {
    setActiveDropdown((prev) => (prev === type ? null : type));
  };

  const closeDropdown = () => setActiveDropdown(null);

  const toggleExpand = () => {
    setIsExpanded((prev) => {
      const next = !prev;
      if (!next) closeDropdown();
      return next;
    });
  };

  const hour = timeData[keyTime]?.hours || "";
  const minute = timeData[keyTime]?.minutes || "";
  const meridiem = timeData[keyTime]?.meridiem || "AM";

  const confirmTime = () => {
    handleTimeConfirm(
      fieldname,
      data_type,
      hour || "00",
      minute || "00",
      meridiem || "AM",
    );
    setIsExpanded(false);
  };

  const clearTime = () => {
    setTimeData((prev) => ({
      ...prev,
      [keyTime]: { hours: "", minutes: "", meridiem: "AM" },
    }));

    handleClearTime(keyTime, fieldname);
    setIsExpanded(false);
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsExpanded(false);
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  const isClearEnabled = hour || minute;

  const isSetTimeEnabled = hour && minute;

  return (
    <div
      ref={wrapperRef}
      className="relative w-full bg-primary-nav-act rounded-border-radius-md"
    >
      <div
        className={`${
          isExpanded
            ? "rounded-t-border-radius-md rounded-b-none"
            : "rounded-border-radius-md z-z-index-base global-hover-white relative"
        } px-padding-md py-padding-sm bg-primary-nav-act cursor-pointer text-text-primary flex justify-between items-center`}
        onClick={toggleExpand}
      >
        <span>{value || "Set time"}</span>

        <div
          className={`
    transition-transform duration-animation-fast-s
    ${
      isExpanded
        ? "arrow_up_anim arrow_base_open"
        : "arrow_down_anim arrow_base_closed"
    }
  `}
        >
          <SvgComponent
            url={ICONS?.ARROW_UP_WHITE}
            width="var(--icon-xs)"
            color="var(--text-primary)"
          />
        </div>
      </div>

      <div
        style={{
          maxHeight: isExpanded ? "var(--max-animation-height-big-ddl)" : "0px",
          opacity: isExpanded ? "var(--base-opacity)" : "var(--opacity-none)",
          transform: isExpanded
            ? "translateY(var(--translate-x-inactive))"
            : "translateY(var(--translate-custom))",
          transition:
            "max-height var(--animation-fast) ease-in-out, opacity var(--animation-fast) ease, transform var(--animation-fast) ease",
        }}
        className="overflow-hidden"
      >
        <div className="bg-primary-bg h-[var(--border)] mx-margin-md"></div>

        <div className="bg-primary-nav-act pt-padding-sm pb-padding-sm px-padding-md rounded-b-border-radius-md w-full">
          <div className="flex items-start gap-gap-sm">
            <div
              className="flex-1 bg-primary-nav rounded-border-radius-md overflow-hidden"
              ref={dropdownRefs.hour}
            >
              <div
                className={`flex items-center justify-between bg-primary-nav rounded-border-radius-md p-padding-sm px-padding-button-l text-text-primary cursor-pointer ${
                  activeDropdown === "hour"
                    ? "rounded-t-border-radius-md rounded-b-none"
                    : "rounded-border-radius-md relative z-z-index-base global-hover-white"
                }`}
                onClick={() => toggleDropdown("hour")}
              >
                <span className="pr-padding-xs">{hour || "HH"}</span>
                <div
                  className={` flex-shrink-0
    transition-transform duration-animation-fast-s
    ${
      activeDropdown === "hour"
        ? "arrow_up_anim arrow_base_open"
        : "arrow_down_anim arrow_base_closed"
    }
  `}
                >
                  <SvgComponent
                    url={ICONS?.ARROW_UP_WHITE}
                    width="var(--icon-xs)"
                    color="var(--text-primary)"
                  />
                </div>
              </div>

              <div
                style={{
                  maxHeight:
                    activeDropdown === "hour"
                      ? "var(--max-animation-height-small-ddl)"
                      : "0px",
                  opacity:
                    activeDropdown === "hour"
                      ? "var(--base-opacity)"
                      : "var(--opacity-none)",
                  transform:
                    activeDropdown === "hour"
                      ? "translateY(var(--translate-x-inactive))"
                      : "translateY(var(--translate-custom))",
                  transition:
                    "max-height var(--animation-fast-s) ease-in-out, opacity var(--animation-fast-s) ease, transform var(--animation-fast-s) ease",
                }}
                className="overflow-y-auto"
              >
                <div className="bg-primary-bg h-[var(--border)] mx-margin-sm"></div>

                <div
                  key={activeDropdown === "hour"}
                  className="hour_scroller rounded-b-border-radius-md rounded-t-none gap-gap-button-s flex flex-col bg-primary-nav max-h-[var(--max-width-list-status-lg)] overflow-y-auto p-padding-sm"
                >
                  {hours.map((h) => {
                    const selected = hour === h;
                    return (
                      <div
                        key={h}
                        className={`py-padding-xs px-padding-sm items-center cursor-pointer flex justify-between text-text-primary rounded-border-radius-md ${
                          selected
                            ? "bg-primary-card"
                            : "relative z-z-index-base global-hover-white"
                        }`}
                        onClick={() => {
                          setTimeData((prev) => ({
                            ...prev,
                            [keyTime]: { ...prev[keyTime], hours: h },
                          }));
                          closeDropdown();
                        }}
                      >
                        {h}
                        <div className="Qsm:block hidden">
                          {selected && (
                            <SvgComponent
                              color="var(--text-primary)"
                              url={ICONS?.CHECK_MARK_WHITE}
                              width="var(--icon-xs)"
                              cl
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <span className="text-text-primary text-xl mt-margin-xs">:</span>

            <div
              className="flex-1 rounded-border-radius-md bg-primary-nav overflow-hidden"
              ref={dropdownRefs.minute}
            >
              <div
                className={`flex items-center justify-between bg-primary-nav p-padding-sm pl-padding-button-l text-text-primary cursor-pointer ${
                  activeDropdown === "minute"
                    ? "rounded-t-border-radius-md rounded-b-none"
                    : "rounded-border-radius-md relative z-z-index-base global-hover-white"
                }`}
                onClick={() => toggleDropdown("minute")}
              >
                <span className="pr-padding-xs">{minute || "MM"}</span>
                <div
                  className={`flex-shrink-0
    transition-transform duration-animation-fast-s
    ${
      activeDropdown === "minute"
        ? "arrow_up_anim arrow_base_open"
        : "arrow_down_anim arrow_base_closed"
    }
  `}
                >
                  <SvgComponent
                    url={ICONS?.ARROW_UP_WHITE}
                    width="var(--icon-xs)"
                    color="var(--text-primary)"
                  />
                </div>
              </div>

              <div
                style={{
                  maxHeight:
                    activeDropdown === "minute"
                      ? "var(--max-animation-height-small-ddl)"
                      : "0px",
                  opacity:
                    activeDropdown === "minute"
                      ? "var(--base-opacity)"
                      : "var(--opacity-none)",
                  transform:
                    activeDropdown === "minute"
                      ? "translateY(var(--translate-x-inactive))"
                      : "translateY(var(--translate-custom))",
                  transition:
                    "max-height var(--animation-fast-s) ease-in-out, opacity var(--animation-fast-s) ease, transform var(--animation-fast-s) ease",
                }}
                className="overflow-y-auto [&::-webkit-scrollbar]:hidden"
              >
                <div className="bg-primary-bg h-[var(--border)] mx-margin-sm"></div>

                <div
                  key={activeDropdown === "minute"}
                  className="minute_scroller bg-primary-nav rounded-b-border-radius-md max-h-[var(--max-width-list-status-lg)] overflow-y-auto p-padding-sm gap-gap-button-s flex flex-col"
                >
                  {minutes.map((m) => {
                    const selected = minute === m;
                    return (
                      <div
                        key={m}
                        className={`py-padding-xs px-padding-sm items-center cursor-pointer flex justify-between text-text-primary rounded-border-radius-md ${
                          selected
                            ? "bg-primary-card"
                            : "relative z-z-index-base global-hover-white"
                        }`}
                        onClick={() => {
                          setTimeData((prev) => ({
                            ...prev,
                            [keyTime]: { ...prev[keyTime], minutes: m },
                          }));
                          closeDropdown();
                        }}
                      >
                        {m}
                        <div className="Qsm:block hidden">
                          {selected && (
                            <SvgComponent
                              color="var(--text-primary)"
                              url={ICONS?.CHECK_MARK_WHITE}
                              width="var(--icon-xs)"
                              cl
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <span className="text-text-primary text-xl mt-margin-xs">:</span>

            <div
              className="flex-1 rounded-border-radius-md bg-primary-nav overflow-hidden"
              ref={dropdownRefs.meridiem}
            >
              <div
                className={`flex items-center justify-between bg-primary-nav p-padding-sm pl-padding-button-l text-text-primary cursor-pointer ${
                  activeDropdown === "meridiem"
                    ? "rounded-t-border-radius-md rounded-b-none"
                    : "rounded-border-radius-md relative z-z-index-base global-hover-white"
                }`}
                onClick={() => toggleDropdown("meridiem")}
              >
                <span className="pr-padding-xs">{meridiem}</span>
                <div
                  className={`flex-shrink-0
    transition-transform duration-animation-fast-s
    ${
      activeDropdown === "meridiem"
        ? "arrow_up_anim arrow_base_open"
        : "arrow_down_anim arrow_base_closed"
    }
  `}
                >
                  <SvgComponent
                    url={ICONS?.ARROW_UP_WHITE}
                    width="var(--icon-xs)"
                    color="var(--text-primary)"
                  />
                </div>
              </div>

              <div
                style={{
                  maxHeight:
                    activeDropdown === "meridiem"
                      ? "var(--max-animation-height-small-ddl)"
                      : "0px",
                  opacity:
                    activeDropdown === "meridiem"
                      ? "var(--base-opacity)"
                      : "var(--opacity-none)",
                  transform:
                    activeDropdown === "meridiem"
                      ? "translateY(var(--translate-x-inactive))"
                      : "translateY(var(--translate-custom))",
                  transition:
                    "max-height var(--animation-fast-s) ease-in-out, opacity var(--animation-fast-s) ease, transform var(--animation-fast-s) ease",
                }}
                className="overflow-hidden"
              >
                <div className="bg-primary-bg h-[var(--border)] mx-margin-sm"></div>

                <div
                  key={activeDropdown === "meridiem"}
                  className=" bg-primary-nav rounded-b-border-radius-md max-h-[var(--max-height-top-list-card)] overflow-y-auto p-padding-sm flex flex-col gap-gap-sm"
                >
                  {meridiems.map((m) => {
                    const selected = meridiem === m;
                    return (
                      <div
                        key={m}
                        className={`py-padding-xs px-padding-sm cursor-pointer flex items-center justify-between text-text-primary rounded-border-radius-md ${
                          selected
                            ? "bg-primary-card"
                            : "relative z-z-index-base global-hover-white"
                        }`}
                        onClick={() => {
                          setTimeData((prev) => ({
                            ...prev,
                            [keyTime]: { ...prev[keyTime], meridiem: m },
                          }));
                          closeDropdown();
                        }}
                      >
                        {m}
                        <div className="Qsm:block hidden">
                          {selected && (
                            <SvgComponent
                              color="var(--text-primary)"
                              url={ICONS?.CHECK_MARK_WHITE}
                              width="var(--icon-xs)"
                              cl
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-gap-md mt-margin-md">
            <div
              onMouseDown={(e) => {
                if (!isClearEnabled) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              onClick={(e) => {
                if (!isClearEnabled) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              className="w-width-full"
            >
              <PrimaryButton
                btnText="Clear"
                btnType="button"
                enabledTextColor="var(--text-primary)"
                outLineColor="var(--text-primary)"
                handleFunction={isClearEnabled ? clearTime : () => {}}
                isDisabled={!isClearEnabled}
                disabledTextColor="var(--text-primary)"
                hoverOption="white"
                isLoaderNeeded={false}
                enabledBgColor="var(--primary-nav-act)"
                disabledBgColor="var(--primary-nav-act)"
                fullWidthRequired
              />
            </div>

            <div
              onMouseDown={(e) => {
                if (!isSetTimeEnabled) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              onClick={(e) => {
                if (!isSetTimeEnabled) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              className="w-width-full"
            >
              <PrimaryButton
                btnText="Set time"
                btnType="button"
                enabledBgColor="var(--text-primary)"
                enabledTextColor="var(--primary-bg)"
                handleFunction={isSetTimeEnabled ? confirmTime : () => {}}
                isDisabled={!isSetTimeEnabled}
                disabledBgColor="var(--text-disabled)"
                disabledTextColor="var(--text-primary)"
                hoverOption="black"
                isLoaderNeeded={false}
                fullWidthRequired
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonTimePicker;
